// Logic THUẦN cho "Lên đơn" / "Lưu khách" / "Khách cũ" từ kết quả bóc đơn (spec 5.4) — không React, không gọi mạng.
// Test ở tests/unit/web-gomdon-prefill.test.ts. Danh mục địa giới mới (findNewArea của @shoptrack/common/admin-areas)
// được truyền vào, để module này không kéo danh mục vào bundle và test dùng thẳng bản thật.

import type { NewArea } from '@shoptrack/common/admin-areas'
import { normalizePhoneVN } from '../../lib/format'
import { customerToSave, type CustomerToSave } from '../../lib/orderLeave'
import type { ChannelType, Conversation, Extraction } from './types'

/** Dữ liệu "Lên đơn" gửi sang /orders/new qua history state (không qua URL) */
export interface GomdonPrefill {
  conversationId: number
  channelType: ChannelType
  customerName: string | null
  phone: string | null
  address: { street: string | null; ward: string | null; province: string | null; full: string | null }
  items: Array<{ name: string; quantity: number; unitPrice: number | null }>
  note: string | null
}

export type FindNewArea = (city: string, ward: string) => NewArea | null

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)

/** Kết quả bóc đơn của hội thoại → state cho "Lên đơn" */
export function buildGomdonPrefill(conversation: Pick<Conversation, 'id' | 'channelType'>, e: Extraction): GomdonPrefill {
  return {
    conversationId: conversation.id,
    channelType: conversation.channelType,
    customerName: str(e.customerName),
    phone: str(e.phone),
    address: { street: str(e.address.street), ward: str(e.address.ward), province: str(e.address.province), full: str(e.address.full) },
    items: e.items
      .filter((it) => str(it.name))
      .map((it) => ({
        name: it.name.trim(),
        quantity: Math.max(1, Math.floor(Number(it.quantity) || 1)),
        unitPrice: typeof it.unitPrice === 'number' && it.unitPrice > 0 ? it.unitPrice : null,
      })),
    note: str(e.note),
  }
}

/**
 * Đọc `location.state.gomdonPrefill` — history state đến từ trình duyệt, không tin: sai dạng thì null
 * (form tạo đơn chạy y như cũ).
 */
export function readGomdonPrefill(state: unknown): GomdonPrefill | null {
  const p = (state as { gomdonPrefill?: unknown } | null | undefined)?.gomdonPrefill as Record<string, unknown> | undefined
  if (!p || typeof p !== 'object') return null
  if (typeof p.conversationId !== 'number' || typeof p.channelType !== 'string') return null
  const a = (p.address && typeof p.address === 'object' ? p.address : {}) as Record<string, unknown>
  const items = Array.isArray(p.items) ? p.items : []
  return {
    conversationId: p.conversationId,
    channelType: p.channelType as ChannelType,
    customerName: str(p.customerName),
    phone: str(p.phone),
    address: { street: str(a.street), ward: str(a.ward), province: str(a.province), full: str(a.full) },
    items: items.flatMap((raw) => {
      const it = (raw ?? {}) as Record<string, unknown>
      const name = str(it.name)
      if (!name) return []
      const quantity = Math.max(1, Math.floor(Number(it.quantity) || 1))
      const unitPrice = typeof it.unitPrice === 'number' && it.unitPrice > 0 ? it.unitPrice : null
      return [{ name, quantity, unitPrice }]
    }),
    note: str(p.note),
  }
}

/** Kênh bán của đơn: Zalo cá nhân → "Zalo", kênh khác → "Other" */
export function gomdonOrderChannel(channelType: string): 'Zalo' | 'Other' {
  return channelType === 'zalo_personal' ? 'Zalo' : 'Other'
}

export interface PrefillAddress {
  /** Ô số nhà / tên đường (không khớp khu vực: cả địa chỉ `full`) */
  street: string
  city: string
  ward: string
  /** Khớp được tỉnh + xã theo địa giới mới */
  matched: boolean
}

/**
 * Địa chỉ bóc được → ô địa chỉ của form. Khớp tỉnh + xã theo địa giới mới (34 tỉnh, 2 cấp, không quận) thì điền thẳng khu vực
 * dạng mới (tỉnh + xã, quận để trống) vào form — KHÁC với chọn tay ở AddressInput, nơi chọn khu vực mới được đổi ngay sang khu
 * vực cũ khi tra được. Đơn lưu nguyên dạng mới; chỉ lúc tạo vận đơn BEST, `buildOrderAddBody` (packages/best) mới quy về khu vực
 * cũ 3 cấp bằng `toOldArea` (tra không ra thì gửi nguyên dạng mới) và ghi kèm địa chỉ mới vào ô địa chỉ. Không khớp thì đưa cả
 * địa chỉ vào ô số nhà để nhân viên sửa.
 */
export function prefillAddress(a: GomdonPrefill['address'], findNewArea: FindNewArea | null): PrefillAddress {
  const nw = a.province && a.ward && findNewArea ? findNewArea(a.province, a.ward) : null
  if (nw) return { street: a.street ?? '', city: nw.city, ward: nw.ward, matched: true }
  const full = a.full ?? [a.street, a.ward, a.province].filter(Boolean).join(', ')
  return { street: full, city: '', ward: '', matched: false }
}

const nameKey = (s: string) => s.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi')

/** Kết quả GET /products?q=<tên>&active=true → sản phẩm trùng tên (không phân biệt hoa thường), hoặc kết quả duy nhất */
export function pickProduct<P extends { name: string }>(name: string, products: readonly P[]): P | null {
  const key = nameKey(name)
  return products.find((p) => nameKey(p.name) === key) ?? (products.length === 1 ? products[0]! : null)
}

export interface PrefillHints {
  /** Ghi chú bóc được từ hội thoại (null = không có) */
  note: string | null
  /** "Chưa khớp sản phẩm: <tên> x<sl>" cho mỗi món không khớp sản phẩm kho */
  unmatched: string[]
}

/**
 * Gợi ý cho nhân viên hiện trong khung thông tin ở form tạo đơn — KHÔNG điền vào ô nào và KHÔNG gửi kèm đơn
 * ("Ghi chú cho bưu tá" in cho shipper nên để trống). null = không có gì để hiện.
 */
export function prefillHints(note: string | null, unmatched: ReadonlyArray<{ name: string; quantity: number }>): PrefillHints | null {
  const n = note?.trim() || null
  const lines = unmatched.map((it) => `Chưa khớp sản phẩm: ${it.name} x${it.quantity}`)
  return n || lines.length ? { note: n, unmatched: lines } : null
}

/**
 * Body "Lưu khách" (POST /customers, createCustomerSchema): tên + SĐT, kèm địa chỉ khi khớp được khu vực và có số nhà.
 * Thiếu tên hoặc SĐT không hợp lệ → null (không lưu được).
 */
export function gomdonCustomerToSave(e: Pick<Extraction, 'customerName' | 'phone' | 'address'>, findNewArea: FindNewArea | null): CustomerToSave | null {
  const a = prefillAddress(
    { street: str(e.address.street), ward: str(e.address.ward), province: str(e.address.province), full: str(e.address.full) },
    findNewArea,
  )
  return customerToSave({
    name: e.customerName ?? '',
    phone: e.phone,
    ...(a.matched ? { street: a.street, ward: a.ward, city: a.city, district: null } : {}),
  })
}

/** Khách ShopTrack trùng đúng SĐT (so dạng chuẩn hoá — dòng cũ có thể lưu +84…) */
export function findSamePhone<C extends { phone: string | null }>(phone: string | null | undefined, customers: readonly C[]): C | null {
  const p = normalizePhoneVN(phone)
  if (!p) return null
  return customers.find((c) => c.phone && normalizePhoneVN(c.phone) === p) ?? null
}
